import { useEffect, useState } from "react";
import {
  createComparatorFirm,
  updateComparatorFirm,
  createFileUploadUrl,
  addFileToDb,
  useAction,
} from "wasp/client/operations";
import type { ComparatorFirm } from "wasp/entities";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "../../../client/components/ui/dialog";
import { Button } from "../../../client/components/ui/button";
import { Input } from "../../../client/components/ui/input";
import { Label } from "../../../client/components/ui/label";
import { Checkbox } from "../../../client/components/ui/checkbox";
import { useToast } from "../../../client/hooks/use-toast";
import { Upload } from "lucide-react";
import { ALLOWED_FILE_TYPES } from "../../../file-upload/validation";

type AllowedFileType = (typeof ALLOWED_FILE_TYPES)[number];

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  firm: ComparatorFirm | null;
  onSaved: () => void;
}

export default function FirmFormDialog({ open, onOpenChange, firm, onSaved }: Props) {
  const createFirm = useAction(createComparatorFirm);
  const updateFirm = useAction(updateComparatorFirm);
  const getUploadUrl = useAction(createFileUploadUrl);
  const addFile = useAction(addFileToDb);
  const { toast } = useToast();

  const [name, setName] = useState("");
  const [slug, setSlug] = useState("");
  const [countryCode, setCountryCode] = useState("");
  const [foundedYear, setFoundedYear] = useState("");
  const [rating, setRating] = useState("");
  const [affiliateUrl, setAffiliateUrl] = useState("");
  const [promoCode, setPromoCode] = useState("");
  const [isPublished, setIsPublished] = useState(true);
  const [sortOrder, setSortOrder] = useState("0");
  const [logoS3Key, setLogoS3Key] = useState<string | null>(null);
  const [logoPreview, setLogoPreview] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    setName(firm?.name ?? "");
    setSlug(firm?.slug ?? "");
    setCountryCode(firm?.countryCode ?? "");
    setFoundedYear(firm?.foundedYear != null ? String(firm.foundedYear) : "");
    setRating(firm?.rating != null ? String(firm.rating) : "");
    setAffiliateUrl(firm?.affiliateUrl ?? "");
    setPromoCode(firm?.promoCode ?? "");
    setIsPublished(firm?.isPublished ?? true);
    setSortOrder(firm?.sortOrder != null ? String(firm.sortOrder) : "0");
    setLogoS3Key(firm?.logoS3Key ?? null);
    setLogoPreview(null);
  }, [firm, open]);

  const handleLogoUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    try {
      const ext = file.name.split(".").pop() ?? "png";
      const fileType = (file.type || "image/png") as AllowedFileType;
      const fileName = `comparator-logo-${Date.now()}.${ext}`;
      const { s3UploadUrl, s3UploadFields, s3Key } = await getUploadUrl({ fileType, fileName });
      const formData = new FormData();
      Object.entries(s3UploadFields).forEach(([k, v]) => formData.append(k, v as string));
      formData.append("file", file);
      await fetch(s3UploadUrl, { method: "POST", body: formData });
      await addFile({ s3Key, fileName, fileType });
      setLogoS3Key(s3Key);
      setLogoPreview(URL.createObjectURL(file));
      toast({ title: "Logo uploaded" });
    } catch (err) {
      toast({ title: (err as Error).message, variant: "destructive" });
    }
  };

  const handleSubmit = async () => {
    setSaving(true);
    try {
      const payload = {
        name,
        slug,
        logoS3Key: logoS3Key ?? undefined,
        countryCode: countryCode.trim() ? countryCode.trim() : undefined,
        foundedYear: foundedYear.trim() ? Number(foundedYear) : undefined,
        rating: rating.trim() ? Number(rating) : undefined,
        affiliateUrl: affiliateUrl.trim() ? affiliateUrl.trim() : undefined,
        promoCode: promoCode.trim() ? promoCode.trim() : undefined,
        isPublished,
        sortOrder: sortOrder.trim() ? Number(sortOrder) : 0,
      };
      if (firm) {
        await updateFirm({ id: firm.id, ...payload });
      } else {
        await createFirm(payload);
      }
      toast({ title: "Saved" });
      onSaved();
      onOpenChange(false);
    } catch (e) {
      toast({ title: (e as Error).message, variant: "destructive" });
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>{firm ? "Edit firm" : "New firm"}</DialogTitle>
        </DialogHeader>

        <div className="space-y-4 py-2">
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="firm-name">Name</Label>
              <Input id="firm-name" value={name} onChange={(e) => setName(e.target.value)} />
            </div>
            <div className="space-y-2">
              <Label htmlFor="firm-slug">Slug</Label>
              <Input
                id="firm-slug"
                value={slug}
                onChange={(e) => setSlug(e.target.value)}
                placeholder="lowercase-with-dashes"
              />
            </div>
          </div>

          <div className="grid grid-cols-3 gap-4">
            <div className="space-y-2">
              <Label htmlFor="firm-country">Country</Label>
              <Input
                id="firm-country"
                value={countryCode}
                onChange={(e) => setCountryCode(e.target.value)}
                maxLength={2}
                placeholder="US"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="firm-founded">Founded</Label>
              <Input
                id="firm-founded"
                type="number"
                value={foundedYear}
                onChange={(e) => setFoundedYear(e.target.value)}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="firm-rating">Rating (0–10)</Label>
              <Input
                id="firm-rating"
                type="number"
                step="0.1"
                min="0"
                max="10"
                value={rating}
                onChange={(e) => setRating(e.target.value)}
              />
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="firm-affiliate">Affiliate URL</Label>
            <Input
              id="firm-affiliate"
              value={affiliateUrl}
              onChange={(e) => setAffiliateUrl(e.target.value)}
              placeholder="https://..."
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="firm-promo">Promo code</Label>
              <Input
                id="firm-promo"
                value={promoCode}
                onChange={(e) => setPromoCode(e.target.value)}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="firm-sort">Sort order</Label>
              <Input
                id="firm-sort"
                type="number"
                value={sortOrder}
                onChange={(e) => setSortOrder(e.target.value)}
              />
            </div>
          </div>

          <div className="space-y-2">
            <Label>Logo</Label>
            <div className="flex items-center gap-3">
              <label className="inline-flex cursor-pointer items-center gap-2 rounded-md border border-border px-3 py-2 text-sm text-foreground hover:bg-accent">
                <Upload className="h-4 w-4" />
                Upload
                <input
                  type="file"
                  accept="image/*"
                  className="hidden"
                  onChange={handleLogoUpload}
                />
              </label>
              {logoPreview ? (
                <img
                  src={logoPreview}
                  alt="Logo preview"
                  className="h-10 w-10 rounded object-contain"
                />
              ) : logoS3Key ? (
                <span className="text-sm text-muted-foreground">Logo saved</span>
              ) : (
                <span className="text-sm text-muted-foreground">No logo</span>
              )}
            </div>
          </div>

          <div className="flex items-center gap-2">
            <Checkbox
              id="firm-published"
              checked={isPublished}
              onCheckedChange={(v) => setIsPublished(v === true)}
            />
            <Label htmlFor="firm-published">Published</Label>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={saving}>
            Cancel
          </Button>
          <Button onClick={handleSubmit} disabled={saving}>
            {saving ? "Saving…" : "Save"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
